import { DocumentReference } from "@firebase/firestore";
import Link from "next/link";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AiOutlineShareAlt, AiOutlineUnorderedList } from "react-icons/ai";
import { BiMailSend } from "react-icons/bi";
import { CgLock } from "react-icons/cg";
import { FaUserAlt } from "react-icons/fa";
import { FiSearch } from "react-icons/fi";
import { HiOutlineMail } from "react-icons/hi";
import { IoMdClose } from "react-icons/io";
import { MdOutlineMarkEmailRead, MdOutlineMarkEmailUnread, MdOutlineMarkunread } from "react-icons/md";
import { RiShareFill } from "react-icons/ri";
import { useHits, useSearchBox } from "react-instantsearch-hooks";
import ReactTooltip from "react-tooltip";
import { EmailInfo, EmailsSent, getUser, getUserIdByNickname, Pack, updatePack, User } from "../../firebase/firestore";
import { ContactList, getContactList } from "../../firebase/firestore/contacts";
import { parseSharedEntryFromFirestore } from "../../firebase/firestore/shared_with";
import { useAuth } from "../../hooks/useAuth";
import { useDebounce } from "../../hooks/useDebounce";
import { useFilteredContactLists } from "../../hooks/useFilteredContactLists";
import { AlgoliaSearchContext } from "../../hooks/useTrackSearch";
import useUserLists from "../../hooks/useUserLists";
import { prettyDate } from "../../utils/dates";
import { isEmail } from "../../utils/string";
import Input from "../inputs/Input";
import { Copyable } from "../misc/Copyable";
import { composePrivateLink } from "./PackPage";
type EmailUser = {
  email: string;
};

export type SearchResult = {
  type: "email" | "user" | "list";
  value: string | User | ContactList;
};

//@ts-ignore
const serializeSearchResult = (sRes: SearchResult) =>
  sRes.type === "email" ? sRes.value : sRes.type === "user" ? sRes.value.id : sRes.value.ref;
type UserResultRowProps = {
  type: "email" | "user" | "list";
  emailInfo?: EmailInfo;
  onDelete?: () => Promise<any>;
  canDelete?: boolean;
  selectable?: boolean;
  value?: string | User | ContactList;

  linkToLists?: boolean;
  showEmailStatus?: boolean;
};
const UserResultRow = ({
  type,
  emailInfo = null,
  onDelete = null,
  canDelete = false,
  selectable = false,
  value,
  linkToLists = false,
  showEmailStatus = true,
  ...rest
}: UserResultRowProps) => {
  const { user } = useAuth();
  const normalTitle = (
    <div className="w-1/4">{type === "email" ? value : type === "user" ? `@${value.nickname}` : value.title}</div>
  );
  const row = (title) => (
    <div
      className={`group py-2 px-4 flex items-center ${selectable && "hover:bg-bgactive_"} cursor-pointer text-sm`}
      {...rest}
    >
      <div className="w-8">
        {type === "list" && <AiOutlineUnorderedList className="w-4 h-4" />}
        {type === "email" && <HiOutlineMail className="w-4 h-4" />}
        {type === "user" && <FaUserAlt className="w-4 h-4" />}
      </div>
      {title}
      <ReactTooltip delayShow={300} place="bottom" effect="solid" />
      <div className="grow ">
        {canDelete && showEmailStatus && (
          <div
            data-tip={
              emailInfo
                ? `Email${emailInfo?.email_ids?.length ? "s" : ""} sent ${prettyDate(emailInfo.date.toDate())}`
                : `Sending email...`
            }
            className="flex items-center justify-center"
          >
            {!emailInfo ? (
              <MdOutlineMarkunread className="w-4 h-4 opacity-40" />
            ) : (
              <MdOutlineMarkEmailRead className="w-4 h-4" />
            )}
          </div>
        )}
      </div>
      {canDelete && onDelete && <IoMdClose onClick={onDelete} className="w-4 h-4 group-hover:opacity-100 opacity-50" />}
    </div>
  );

  if (type === "list" && linkToLists) {
    const contactList = value as ContactList;
    const contactOwnerId = contactList?.ref?.path?.split("/")[1];
    if (contactOwnerId === user?.id) {
      return row(<Link href={`/contacts/list/${contactList.ref.id}`}>{normalTitle}</Link>);
    } else {
      return row(normalTitle);
    }
  } else return row(normalTitle);
};

const selectedUserToEmailsSentKey = (u: SearchResult): string => {
  if (u.type === "email") return u.value as string;
  if (u.type === "user") return (u.value as User).uid || (u.value as User).id;
  if (u.type === "list") return (u.value as ContactList).ref.path;
};
export const SelectUsers = (props) => (
  <AlgoliaSearchContext index={"users"}>
    <_SelectUsers {...props} />
  </AlgoliaSearchContext>
);

function _SelectUsers({
  selectedUsersSerialized,
  selectedUsers,
  emailsSent = {},
  copyPrivateLink = null,
  privateLink,
  className = "",
  setSelectedUsers,
  placeholder,
  showContactLists = true,
  canSelectExternalEmail = true,
  showEmailStatus = true,
  ...rest
}: {
  selectedUsersSerialized: string[];
  selectedUsers: SearchResult[];
  emailsSent?: EmailsSent;
  setSelectedUsers: (newUsers: string[]) => Promise<any>;
  placeholder?: string;

  // If true, can type in email of non-beatpacks user and select it as {type: "email", value: "a@b.com"}
  canSelectExternalEmail?: boolean;
  [key: string]: any;
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const { user } = useAuth();

  // todo: integrate typesense or algolia and remove the debouncing

  const { query, refine, isSearchStalled } = useSearchBox();
  // const debouncedSearchTerm = useDebounce(searchTerm, 300)
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);

  const { hits } = useHits();
  const showHits = query?.length > 0 && hits?.length;

  const [contactLists, contactListsLoading, contactListsError] = useUserLists(user?.uid);
  const extendedContactLists = useMemo(
    () => contactLists?.map((c) => ({ ...c, hiddenField: "email mail list" })) || [],
    [contactLists]
  );
  const { sortedFilteredLists } = useFilteredContactLists(extendedContactLists || [], searchTerm);
  useEffect(() => console.log(contactLists), [contactLists]);
  // const sortedFilteredLists = [];
  useEffect(() => {
    (async () => {
      let newResults = [];
      if (!searchTerm?.length) {
      } else if (isEmail(searchTerm)) {
        if (canSelectExternalEmail) {
          newResults = [
            {
              type: "email",
              value: searchTerm,
            },
          ];
        }
      } else {
        // interpret as nickname search
        if (searchTerm !== user.nickname) {
          newResults = hits.map((hit) => ({
            type: "user",
            value: { id: hit.objectID, ...hit },
          }));
        }
      }

      newResults = newResults.filter(
        (newRes) => !(selectedUsersSerialized || []).includes(serializeSearchResult(newRes))
      );

      // add results from contact lists
      if (showContactLists) {
        newResults = sortedFilteredLists.map((list) => ({ type: "list", value: list })).concat(newResults);
      }
      setSearchResults(newResults);
    })();
    // todo: query familiar users and add them to search results
  }, [searchTerm]);

  const selectUser = (searchRes: SearchResult) => {
    setSelectedUsers((selectedUsersSerialized || []).concat([serializeSearchResult(searchRes)]));
    refine("");
  };

  const deselectUser = (user: SearchResult) => {
    setSelectedUsers(
      (selectedUsersSerialized || []).filter(
        (oldUser) => oldUser !== serializeSearchResult(user) && user.type === "list" && oldUser?.id !== user.value?.id
      )
    );
  };

  // Track when the value coming from the React state changes to synchronize
  // it with InstantSearch.
  useEffect(() => {
    if (query !== searchTerm) {
      refine(searchTerm);
    }
  }, [searchTerm, refine]);

  // Track when the InstantSearch query changes to synchronize it with
  // the React state.
  useEffect(() => {
    // Bypass the state update if the input is focused to avoid concurrent
    // updates when typing.
    if (document.activeElement !== inputRef.current && query !== searchTerm) {
      setSearchTerm(query);
    }
  }, [query]);

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    event.stopPropagation();

    if (inputRef.current) {
      inputRef.current.blur();
    }
  }

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Enter") handleSubmit(e);
  }, []);

  return (
    <div
      {...rest}
      className={`${className} w-full relative bg-bgpassive text-white shadow-md overflow-hidden rounded-md`}
    >
      <div className="flex items-center border-b-light-gray border-b-2 border-co border-opacity-30">
        <FiSearch className="ml-4" />
        <Input
          spellCheck={false}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          maxLength={512}
          type="search"
          onKeyDown={handleKeyDown}
          ref={inputRef}
          placeholder={placeholder || "Search nicknames, emails, or lists"}
          className={`outline-none no-bottom-rounded bg-transparent text-white `}
          onChange={(e) => setSearchTerm(e.target.value)}
          value={searchTerm}
        />
      </div>
      <div className="relative">
        <div
          style={searchTerm ? { height: `100%` } : {}}
          className={` ${
            searchTerm ? " opacity-100" : "h-0 opacity-0 "
          } z-30 bg-dark-gray absolute left-0 top-0 w-full transition-all duration-300 ease-out`}
        >
          {searchTerm &&
            searchResults.map((res, idx) => (
              <UserResultRow selectable onClick={() => selectUser(res)} {...res} key={`user-result-${idx}`} />
            ))}
        </div>
        <div className={`relative h-32  bg-bgpassive overflow-auto`}>
          {selectedUsers.map((selectedUser, idx) => (
            <UserResultRow
              emailInfo={emailsSent[selectedUserToEmailsSentKey(selectedUser)] || null}
              canDelete
              onDelete={() => deselectUser(selectedUser)}
              {...selectedUser}
              linkToLists
              key={`user-result-${idx}`}
              showEmailStatus={showEmailStatus}
            />
          ))}
        </div>

        {privateLink && selectedUsers?.length !== 0 && (
          <div className=" border-t-2 border-t-solid border-t-light-gray border-opacity-30 h-10 flex justify-end">
            <Copyable
              icon={<CgLock fill="white" stroke="white" color="white" className="mr-1 w-3 h-3" />}
              className="cursor-pointer hover:bg-opacity-20 transition-all w-min hover:bg-white rounded-md text-sm items-center bg-opacity-10 flex px-2 py-1 my-1.5 mx-2"
              onClick={() => copyPrivateLink && copyPrivateLink()}
            >
              <div className=" whitespace-nowrap ">Copy private link </div>
            </Copyable>
          </div>
        )}
      </div>
    </div>
  );
}

interface IPackShareSectionProps {
  pack: Pack;
  packRef: DocumentReference<Pack>;
  copyPrivateLink?: () => Promise<void>;
  showContactLists?: boolean;
  canSelectUsers?: boolean; // currently unused
}
export function PackShareSection({
  pack,
  packRef,
  copyPrivateLink,
  showContactLists,
  canSelectUsers,
  ...rest
}: IPackShareSectionProps) {
  const { user } = useAuth();

  // todo: get user avatar and display more info, not just nickname
  const [selectedUsers, setSelectedUsers] = useState<SearchResult[]>([]);
  useEffect(() => {
    (async () =>
      setSelectedUsers(
        (await Promise.all((pack?.shared_with || []).map(parseSharedEntryFromFirestore))).filter((u) => u)
      ))();
  }, [pack?.shared_with]);

  const submitSelectedUsers = async (newSelected: string[]) => {
    updatePack(packRef, {
      shared_with: newSelected,
    });
  };
  // USQRM4ZjX1X67sNHLTCyfeyP6nE3
  // YNveWEKDAxK7rQmAGZGz

  return (
    <div className="text-left p-2 w-full max-w-2xl  z-20">
      <div className="flex items-center text-3xl">
        <AiOutlineShareAlt className="mr-2" />
        Share
      </div>

      <div className="p-2">
        <p className=" text-sm">
          Emails will be sent out to the people you select instantly. These people can access this pack via a private
          link and view it on their <i>Shared with me</i>.
        </p>
        <div className="flex">
          <SelectUsers
            showContactLists={showContactLists}
            emailsSent={pack.emails_sent}
            copyPrivateLink={copyPrivateLink}
            privateLink={pack?.private_link}
            className={`mt-2`}
            selectedUsersSerialized={pack?.shared_with}
            selectedUsers={selectedUsers}
            setSelectedUsers={submitSelectedUsers}
          />
        </div>
      </div>
    </div>
  );
}
